import { InjectionKey, inject } from 'vue';

import {
  AccessibilityDto,
  AmenitiesDto,
  ParkingDto,
  PetPolicyDto,
  PropertyLeaseConditionsDto,
  PropertyLocationDto,
  PropertyRenterRequirementsDto,
} from '@tallo/property';

import { SaveAccessibilityDto } from '../dtos/save-accessibility.dto.interface';
import { SaveAmenitiesDto } from '../dtos/save-amenities.dto.interface';
import { SaveLeaseConditionsDto } from '../dtos/save-lease-condition.dto.interface';
import { SaveLocationDto } from '../dtos/save-location.dto.interface';
import { SaveParkingDto } from '../dtos/save-parking.dto.interface';
import { SavePetPolicyDto } from '../dtos/save-pet-policy.dto.interface';
import { SavePropertyRenterRequirementsDto } from '../dtos/save-renter-requirements.dto.interface';
import { SaveTourTypesDto } from '../dtos/save-tour-types.dto.interface';
import { usePropertyStore } from '../stores/property.store';

/** The slice of a property or a building the shared section forms read: same names on both */
export interface DetailsHolderRecord {
  location?: PropertyLocationDto;
  amenities?: AmenitiesDto | null;
  parking?: ParkingDto | null;
  accessibility?: AccessibilityDto | null;
  renterRequirements?: PropertyRenterRequirementsDto | null;
  petPolicy?: PetPolicyDto | null;
  leaseConditions?: PropertyLeaseConditionsDto | null;
  /** A building's default tours as one object; a property carries its own as top-level flags the form reads from the store */
  tourTypes?: { allowsInPersonTours: boolean; allowsVirtualTours: boolean; allowsSelfGuidedTours: boolean } | null;
}

/**
 * What the amenities, parking and accessibility forms need from the page that hosts them. The
 * property page is the default; the building page provides its own, so one form edits a unit's
 * parking and a building's parking without knowing which it is on.
 */
export interface DetailsHolder {
  readonly kind: 'property' | 'building';
  readonly details: DetailsHolderRecord | undefined;
  /** `portfolioId` files a unit being created; nothing else passes one */
  saveLocation(payload: SaveLocationDto, portfolioId?: string): Promise<unknown>;
  saveAmenities(payload: SaveAmenitiesDto): Promise<unknown>;
  saveParking(payload: SaveParkingDto): Promise<unknown>;
  saveAccessibility(payload: SaveAccessibilityDto): Promise<unknown>;
  saveRenterRequirements(payload: SavePropertyRenterRequirementsDto): Promise<unknown>;
  savePetPolicy(payload: SavePetPolicyDto): Promise<unknown>;
  /** The lease terms and the rent, deposits and fees share one row; a building's never holds a rent or a move-in date */
  saveLeaseConditions(payload: SaveLeaseConditionsDto): Promise<unknown>;
  /** A building's keeps the three kinds only; the access method and ID checks are each unit's own */
  saveTourTypes(payload: SaveTourTypesDto): Promise<unknown>;
}

export const detailsHolderKey: InjectionKey<DetailsHolder> = Symbol('details-holder');

/** The property store, unless the page provided another holder */
export function useDetailsHolder(): DetailsHolder {
  const provided = inject(detailsHolderKey, null);

  if (provided) {
    return provided;
  }

  const propertyStore = usePropertyStore();

  return {
    kind: 'property',
    get details() {
      return propertyStore.property;
    },
    saveLocation: (payload, portfolioId) => propertyStore.saveLocation(payload, portfolioId),
    saveAmenities: (payload) => propertyStore.saveAmenities(payload),
    saveParking: (payload) => propertyStore.saveParking(payload),
    saveAccessibility: (payload) => propertyStore.saveAccessibility(payload),
    saveRenterRequirements: (payload) => propertyStore.saveRenterRequirements(payload),
    savePetPolicy: (payload) => propertyStore.savePetPolicy(payload),
    saveLeaseConditions: (payload) => propertyStore.saveLeaseConditions(payload),
    saveTourTypes: (payload) => propertyStore.saveTourTypes(payload),
  };
}
